import { useEffect, useRef, useState } from "react";
import { useMotion } from "./MotionProvider";
import { useRafLoop, useTimers } from "./hooks";

// 06 P&L odometer — each digit rolls to its new value
export function Odometer({ text, className = "", testid }) {
  const { mode } = useMotion();
  const chars = String(text ?? "—").split("");
  if (mode === "off") return <span className={`num ${className}`} data-testid={testid}>{text ?? "—"}</span>;
  return (
    <span className={`num inline-flex overflow-hidden leading-none ${className}`} data-testid={testid} aria-label={text}>
      {chars.map((c, i) => (/\d/.test(c)
        ? <span key={chars.length - i} className="relative inline-block h-[1em] overflow-hidden" aria-hidden="true">
            <span className="flex flex-col transition-transform duration-[320ms]" style={{ transform: `translateY(-${Number(c) * 10}%)`, transitionTimingFunction: "var(--m-ease-out)" }}>
              {"0123456789".split("").map((d) => <span key={d} className="h-[1em] leading-none">{d}</span>)}
            </span>
          </span>
        : <span key={`${chars.length - i}${c}`} aria-hidden="true">{c}</span>))}
    </span>
  );
}

// Tween a number (used by 08 margin and 28 stats)
export function useTween(value, ms = 450) {
  const { mode } = useMotion();
  const [v, setV] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    if (value == null || mode === "off" || from.current == null) { setV(value); from.current = value; return undefined; }
    const a = from.current;
    const t0 = performance.now();
    let raf = 0;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / ms);
      const e = 1 - (1 - k) ** 3;
      setV(a + (value - a) * e);
      if (k < 1) raf = requestAnimationFrame(step); else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); from.current = value; };
  }, [value, ms, mode]);
  return v;
}

// 17 Quote decode — glyphs settle into the real value (runs only when `runKey` changes)
const GLYPHS = "0123456789#%$&*+=";
export function QuoteDecode({ text, runKey, className = "", testid }) {
  const { allows } = useMotion();
  const [shown, setShown] = useState(text);
  const ran = useRef(null);
  const latest = useRef(text);
  latest.current = text;
  useEffect(() => {
    if (!text || ran.current === runKey || !allows(17)) { setShown(text); return undefined; }
    ran.current = runKey;
    let f = 0;
    const id = setInterval(() => {
      f += 1;
      const s = String(latest.current);
      const settled = Math.floor((f / 14) * s.length);
      setShown(s.split("").map((c, i) => (i < settled || !/[0-9A-Za-z]/.test(c) ? c : GLYPHS[(i * 7 + f * 3) % GLYPHS.length])).join(""));
      if (f >= 14) { clearInterval(id); setShown(latest.current); }
    }, 45);
    return () => clearInterval(id);
  }, [runKey, text, allows]);
  useEffect(() => { if (ran.current === runKey) setShown((s) => (/[#%$&*+=]/.test(s) ? s : text)); }, [text, runKey]);
  return <span className={`font-mono-plex ${className}`} data-testid={testid}>{shown}</span>;
}

// 24 Payoff morph — interpolate y-values of a fixed x-grid to the new curve
export function useMorph(pts, keys, ms = 520) {
  const { mode } = useMotion();
  const [out, setOut] = useState(pts);
  const prev = useRef(pts);
  useEffect(() => {
    if (!pts) return undefined;
    const a = prev.current;
    if (!pts || !a || a.length !== pts.length || mode === "off") { setOut(pts); prev.current = pts; return undefined; }
    const t0 = performance.now();
    let raf = 0;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / ms);
      const e = k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2;
      setOut(pts.map((p, i) => { const o = { ...p }; keys.forEach((key) => { o[key] = a[i][key] + (p[key] - a[i][key]) * e; }); return o; }));
      if (k < 1) raf = requestAnimationFrame(step); else prev.current = pts;
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); prev.current = pts; };
  }, [pts]); // eslint-disable-line react-hooks/exhaustive-deps
  return out;
}

// 18 Magnetic button — only for fine pointers
export function Magnetic({ children, className = "", strength = 0.3, ...rest }) {
  const ref = useRef(null);
  const { allows } = useMotion();
  const fine = typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches;
  const move = (e) => {
    if (!fine || !allows(18) || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * strength}px, ${(e.clientY - r.top - r.height / 2) * strength}px)`;
  };
  const leave = () => { if (ref.current) ref.current.style.transform = ""; };
  return <button ref={ref} onMouseMove={move} onMouseLeave={leave} className={`transition-transform duration-300 ${className}`} style={{ transitionTimingFunction: "var(--m-spring)" }} {...rest}>{children}</button>;
}

// 20 Spotlight border — radial highlight that follows the pointer
export function Spotlight({ children, className = "", ...rest }) {
  const ref = useRef(null);
  const { allows } = useMotion();
  const move = (e) => {
    if (!ref.current || !allows(20)) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--sx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--sy", `${e.clientY - r.top}px`);
  };
  return <div ref={ref} onMouseMove={move} className={`spotlight ${className}`} {...rest}>{children}</div>;
}

// 19 3D tilt (desktop hover only)
export function Tilt({ children, className = "", ...rest }) {
  const ref = useRef(null);
  const { allows } = useMotion();
  const fine = typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches;
  const move = (e) => {
    if (!fine || !allows(19) || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    ref.current.style.transform = `perspective(900px) rotateY(${x * 14}deg) rotateX(${-y * 12}deg) translateZ(0)`;
    ref.current.style.setProperty("--gx", `${(x + 0.5) * 100}%`);
    ref.current.style.setProperty("--gy", `${(y + 0.5) * 100}%`);
  };
  const leave = () => { if (ref.current) ref.current.style.transform = ""; };
  return <div ref={ref} onMouseMove={move} onMouseLeave={leave} className={`tilt-card transition-transform duration-500 ${className}`} style={{ transitionTimingFunction: "var(--m-ease-out)" }} {...rest}>{children}</div>;
}

// Canvas helper: sizes to parent with devicePixelRatio and runs a paused-when-hidden loop
export function MotionCanvas({ n, draw, className = "", density = 1 }) {
  const ref = useRef(null);
  const { allows } = useMotion();
  const on = allows(n);
  const state = useRef({});
  useEffect(() => {
    const c = ref.current;
    if (!c) return undefined;
    const fit = () => {
      const r = c.parentElement.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      c.width = r.width * dpr; c.height = r.height * dpr;
      c.style.width = `${r.width}px`; c.style.height = `${r.height}px`;
      state.current = { ...state.current, w: r.width, h: r.height, dpr, density, init: false };
      if (!on) { const ctx = c.getContext("2d"); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); draw(ctx, 0, state.current); }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(c.parentElement);
    return () => ro.disconnect();
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps
  useRafLoop(ref, (t) => {
    if (!ref.current) return;
    const ctx = ref.current.getContext("2d");
    ctx.setTransform(state.current.dpr, 0, 0, state.current.dpr, 0, 0);
    draw(ctx, t, state.current);
  }, on);
  return <canvas ref={ref} className={`pointer-events-none ${className}`} aria-hidden="true" />;
}

// deterministic pseudo-random for decorative layouts (no Math.random)
export function seeded(seed) {
  let s = seed >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}

export { useTimers };
